class ConfiguratorProductForm extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.stickyButton = document.querySelector(
      '.h-20.fixed.inset-x-0.bottom-0 button'
    );
    this.addOnElements = document.querySelectorAll('[data-add-on-id]');
    this.variantInput = document.querySelector('[data-current-variant-id]');
    this.variantElement = document.querySelectorAll('[data-confg-variant-id]');
    this.selectedAddOns = new Set();
    this.compulsoryAddOns = new Map(); // Track compulsory add-ons by variant
    this.currentCompulsoryAddOn = null; // Track the current compulsory add-on
    this.metaobjectData = this.getMetaobjectData();
    this.variantMetafields = this.getVariantMetafields();
    this.freeGiftIds = this.getFreeGiftIds();

    this.updateVariantId();
    this.setInitialVariantState();
    this.handleVariantClick();
    this.handleAddOnClick();
    this.trackVariantChange();
    if (this.button) {
      this.button.addEventListener('click', this.handleButtonClick.bind(this));
    }

    if (this.stickyButton) {
      this.stickyButton.addEventListener(
        'click',
        this.handleButtonClick.bind(this)
      );
    }
  }

  getFreeGiftIds() {
    // Find all free gift inputs (they have disabled attribute and are in products marked as free gifts)
    const freeGiftInputs = document.querySelectorAll(
      '.free-gift-wrapper input[disabled]'
    );
    const freeGiftIds = [];

    freeGiftInputs.forEach((input) => {
      const addOnId = input.getAttribute('data-add-on-id');
      if (addOnId) {
        freeGiftIds.push(addOnId);
      }
    });

    return freeGiftIds;
  }

  getMetaobjectData() {
    const metaobjectElements = document.querySelectorAll(
      "[class^='metaobject-content-']"
    );
    let metaobjectData = {};
    metaobjectElements.forEach((element, index) => {
      const metaobjectIds =
        element
          .querySelector('[data-metaobject-ids]')
          ?.getAttribute('data-metaobject-ids')
          ?.split(',')
          ?.map((id) => id.trim()) || [];

      const combinedProdId =
        element
          .querySelector('[data-combined-prod-id]')
          ?.getAttribute('data-combined-prod-id') || '';
      metaobjectData[`metaobject_${index + 1}`] = {
        metaobjectIds,
        combinedProdId,
      };
    });
    return metaobjectData;
  }

  getVariantMetafields() {
    const variantMetafieldsContainer = document.getElementById(
      'variant-metafields-data'
    );
    if (!variantMetafieldsContainer) return {};

    try {
      return JSON.parse(variantMetafieldsContainer.textContent);
    } catch (e) {
      console.error('Error parsing variant metafields:', e);
      return {};
    }
  }

  handleAddOnClick() {
    this.addOnElements.forEach((element) => {
      // Add event listener directly to the input element for better control
      const input = element.querySelector('input');
      if (input) {
        // Capture the original click event on the input to prevent state changes
        input.addEventListener(
          'click',
          (event) => {
            const addOnId = element.getAttribute('data-add-on-id');

            // Check if this is the current compulsory add-on
            if (addOnId === this.currentCompulsoryAddOn && input.checked) {
              // If trying to uncheck a compulsory add-on, prevent it
              event.preventDefault();
              console.log('This add-on is compulsory and cannot be unselected');
              return false;
            }
          },
          true
        ); // Use capturing phase to intercept before default behavior
      }

      // Then handle the container click (which would normally toggle the selection)
      element.addEventListener('click', (event) => {
        const id = element.getAttribute('data-add-on-id');
        const inputElement = element.querySelector('input');
        const addOnBorder = element.parentElement?.querySelector(
          '#configurator-card-border'
        );

        // Check if this is the current compulsory add-on
        if (id === this.currentCompulsoryAddOn && this.selectedAddOns.has(id)) {
          // If it's compulsory and already selected, don't allow unselecting
          console.log('This add-on is compulsory and cannot be unselected');
          // Make sure the input stays checked
          if (inputElement) inputElement.checked = true;
          return;
        }

        // Normal toggle behavior for non-compulsory add-ons
        if (this.selectedAddOns.has(id)) {
          this.selectedAddOns.delete(id);
          element.classList.remove('selected');
          if (inputElement) inputElement.classList.remove('selected');
          addOnBorder?.classList.remove('add-border-3px');
        } else {
          this.selectedAddOns.add(id);
          element.classList.add('selected');
          if (inputElement) inputElement.classList.add('selected');
          addOnBorder?.classList.add('add-border-3px');
        }
      });
    });

    // Add a more aggressive prevention by disabling the checkbox changes
    document.addEventListener(
      'change',
      (event) => {
        if (
          (event.target.closest('[data-add-on-id]') &&
            event.target.type === 'checkbox') ||
          event.target.type === 'radio'
        ) {
          const addOnElement = event.target.closest('[data-add-on-id]');
          const addOnId = addOnElement.getAttribute('data-add-on-id');

          if (addOnId === this.currentCompulsoryAddOn) {
            // Force the checkbox to stay checked
            event.target.checked = true;
            event.preventDefault();
          }
        }
      },
      true
    );
  }

  handleVariantClick() {
    this.variantElement.forEach((varElement) => {
      varElement.addEventListener('click', (event) => {
        const variantBorder = event.currentTarget.parentElement?.querySelector(
          '#configurator-card-border'
        );

        if (variantBorder) {
          document
            .querySelectorAll('variant-radios #configurator-card-border')
            .forEach((border) => {
              border.classList.remove('add-border-3px');
            });
          variantBorder.classList.add('add-border-3px');
        }

        // Get the selected variant ID or value
        const variantValue = event.currentTarget.getAttribute('value');
        console.log('Variant clicked - value attribute:', variantValue);

        // Check for and handle compulsory add-ons
        this.handleCompulsoryAddOn(variantValue);
      });
    });
  }

  // Updated method to handle compulsory add-ons
  handleCompulsoryAddOn(variantValue) {
    if (!this.variantMetafields || !variantValue) return;

    // Need to find the actual variant ID based on the option value
    const variantData = this.findVariantByOption(variantValue);

    if (variantData && variantData.id) {
      const variantId = variantData.id;
      this.currentVariantId = variantId;
      console.log('Selected variant ID:', this.currentVariantId);

      // If we have a current compulsory add-on, unselect it first
      if (this.currentCompulsoryAddOn) {
        this.unselectAddOn(this.currentCompulsoryAddOn);
        this.currentCompulsoryAddOn = null;
      }

      // Check if this variant has a compulsory add-on metafield
      if (
        this.variantMetafields[variantId] &&
        this.variantMetafields[variantId].custom &&
        this.variantMetafields[variantId].custom.compulsory_add_on
      ) {
        const addOnProductId =
          this.variantMetafields[variantId].custom.compulsory_add_on;
        console.log(
          'Compulsory Add-On Product ID for variant',
          variantId,
          ':',
          addOnProductId
        );

        // Check if this add-on is present on the page
        const isAddOnPresent = this.isAddOnPresentOnPage(addOnProductId);
        console.log('Compulsory add-on present on page:', isAddOnPresent);

        if (isAddOnPresent) {
          // Store as compulsory for this variant
          this.compulsoryAddOns.set(variantId, addOnProductId);
          this.currentCompulsoryAddOn = addOnProductId;

          // Auto-select the compulsory add-on
          this.selectAddOn(addOnProductId);
        }
      }
    }
  }

  // New method to unselect an add-on
  unselectAddOn(addOnId) {
    // Remove from selected add-ons set
    this.selectedAddOns.delete(addOnId);

    // Find and visually unselect the add-on
    const addOnElement = document.querySelector(
      `[data-add-on-id="${addOnId}"]`
    );
    if (addOnElement) {
      addOnElement.classList.remove('selected');

      const inputElement = addOnElement.querySelector('input');
      if (inputElement) {
        inputElement.checked = false;
        inputElement.classList.remove('selected');
      }

      const addOnBorder = addOnElement.parentElement?.querySelector(
        '#configurator-card-border'
      );
      if (addOnBorder) {
        addOnBorder.classList.remove('add-border-3px');
      }

      // Remove the compulsory indicator
      addOnElement
        .closest('.add-on-wrapper-flex')
        ?.classList.remove('compulsory-add-on');
    }
  }

  // New method to select an add-on
  selectAddOn(addOnId) {
    // Add to selected add-ons set
    this.selectedAddOns.add(addOnId);

    // Find and visually select the add-on
    const addOnElement = document.querySelector(
      `[data-add-on-id="${addOnId}"]`
    );
    if (addOnElement) {
      addOnElement.classList.add('selected');

      const inputElement = addOnElement.querySelector('input');
      if (inputElement) {
        inputElement.checked = true;
        inputElement.classList.add('selected');

        // If this is compulsory, add a special class
        if (addOnId === this.currentCompulsoryAddOn) {
          addOnElement.classList.add('compulsory-locked');
          // We can't fully disable the input as it needs to be submitted
          // but we can make it appear disabled to users
          inputElement.setAttribute('data-locked', 'true');
        }
      }

      const addOnBorder = addOnElement.parentElement?.querySelector(
        '#configurator-card-border'
      );
      if (addOnBorder) {
        addOnBorder.classList.add('add-border-3px');
      }

      // Add a visual indicator that this is compulsory
      const wrapperElement = addOnElement.closest('.add-on-wrapper-flex');
      if (wrapperElement) {
        wrapperElement.classList.add('compulsory-add-on');
      }
    }
  }

  // Check if add-on is present on page
  isAddOnPresentOnPage(addOnProductId) {
    if (!addOnProductId) return false;

    // Look for any element with this add-on ID
    const addOnElements = document.querySelectorAll(
      `[data-add-on-id="${addOnProductId}"]`
    );

    // If we can't find it by data-add-on-id, check other possible locations
    if (addOnElements.length === 0) {
      // Check if there are any configurator cards with this ID
      const configuratorCards = document.querySelectorAll(
        '.add-on-wrapper-flex input'
      );
      for (const card of configuratorCards) {
        if (card.getAttribute('data-add-on-id') === addOnProductId) {
          return true;
        }
      }
      return false;
    }

    return addOnElements.length > 0;
  }

  findVariantByOption(optionValue) {
    const variantsData = document.getElementById('product-variants-data');
    if (!variantsData) return null;

    try {
      const variants = JSON.parse(variantsData.textContent);
      return variants.find((variant) => {
        return (
          variant.options.includes(optionValue) ||
          variant.id.toString() === optionValue.toString()
        );
      });
    } catch (e) {
      console.error('Error finding variant by option:', e);
      return null;
    }
  }

  setInitialVariantState() {
    // No automatic border addition on page load
    // User will need to click to select a variant
  }

  getVariantIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  getDefaultVariantId() {
    return this.variantInput
      ? this.variantInput.getAttribute('data-current-variant-id')
      : null;
  }

  updateVariantId() {
    let variantId = this.getVariantIdFromUrl() || this.getDefaultVariantId();

    if (variantId) {
      this.selectedAddOns.delete(this.currentVariantId);
      this.selectedAddOns = new Set([variantId, ...this.selectedAddOns]);
      this.currentVariantId = variantId;
    }
  }

  trackVariantChange() {
    window.addEventListener('popstate', () => this.updateVariantId());

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    const handleHistoryChange = () => {
      setTimeout(() => this.updateVariantId(), 100);
    };

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleHistoryChange();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      handleHistoryChange();
    };
  }

  handleButtonClick(event) {
    event.preventDefault();

    //display loading spinner
    const loader = this.button.querySelector('#atc-btn__loader');

    loader.style.display = 'inline-block';

    // Get the currently selected variant ID
    const selectedVariantId = this.currentVariantId;
    console.log('Selected variant ID in handleButtonClick:', selectedVariantId);

    if (!selectedVariantId) {
      console.error('No variant selected');
      return;
    }

    // Start with the selected variant
    let formData = {
      items: [
        {
          id: parseInt(selectedVariantId, 10),
          quantity: 1,
        },
      ],
    };

    // Log selected add-ons
    console.log('Current selected add-ons:', Array.from(this.selectedAddOns));

    // Add selected add-ons (except the main product variants)
    this.selectedAddOns.forEach((addOnId) => {
      // Make sure it's not a variant of the main product
      // This assumes you have a way to distinguish add-ons from main product variants
      if (addOnId != selectedVariantId && this.isAddonProduct(addOnId)) {
        formData.items.push({
          id: parseInt(addOnId, 10),
          quantity: 1,
        });
      }
    });

    console.log(
      'Form data before checking for combined products:',
      JSON.parse(JSON.stringify(formData))
    );
    console.log('Available metaobject data:', this.metaobjectData);

    // Create an array of IDs to compare (the selected variant + add-ons)
    const selectedIds = formData.items.map((item) => item.id.toString());

    // Track the best match (most items matched)
    let bestMatch = {
      combinedProdId: null,
      matchedIds: [],
      matchCount: 0,
    };

    // Check all metaobjects for potential matches
    Object.values(this.metaobjectData).forEach((entry, index) => {
      const metaobjectIds = entry.metaobjectIds;

      console.log(`Comparing metaobject entry ${index + 1}:`, {
        metaobjectIds: metaobjectIds,
        selectedIds: selectedIds,
        combinedProdId: entry.combinedProdId,
      });

      // Find matching IDs between the metaobject and selected items
      const matchedIds = metaobjectIds.filter((id) => selectedIds.includes(id));

      // Check if this is a complete match
      const isCompleteMatch =
        metaobjectIds.length === matchedIds.length &&
        metaobjectIds.length === selectedIds.length;

      if (isCompleteMatch) {
        console.log(
          `Found exact matching combined product:`,
          entry.combinedProdId
        );
        // Complete match takes priority
        bestMatch = {
          combinedProdId: entry.combinedProdId,
          matchedIds: matchedIds,
          matchCount: matchedIds.length,
          isComplete: true,
        };
        // Exit the loop since we found a complete match
        return;
      }

      // Check if all metaobject IDs are included in selected IDs
      // This is a partial match where the metaobject IDs are a subset of selected IDs
      const isPartialMatch = metaobjectIds.length === matchedIds.length;

      if (isPartialMatch && matchedIds.length > bestMatch.matchCount) {
        console.log(
          `Found partial matching combined product:`,
          entry.combinedProdId
        );
        bestMatch = {
          combinedProdId: entry.combinedProdId,
          matchedIds: matchedIds,
          matchCount: matchedIds.length,
          isComplete: false,
        };
      }
    });

    // If we have a matched combined product (complete or partial)
    if (bestMatch.combinedProdId) {
      if (bestMatch.isComplete) {
        console.log(
          `Using combined product ID: ${bestMatch.combinedProdId} instead of all individual items`
        );
        // For complete match, replace all items
        formData = {
          items: [
            {
              id: parseInt(bestMatch.combinedProdId, 10),
              quantity: 1,
            },
          ],
        };
      } else {
        console.log(
          `Using combined product ID: ${bestMatch.combinedProdId} for ${bestMatch.matchCount} matched items`
        );

        // For partial match, create a new array with:
        // 1. The combined product
        // 2. Any remaining selected items that aren't part of the match
        const remainingItems = formData.items.filter(
          (item) => !bestMatch.matchedIds.includes(item.id.toString())
        );

        console.log('Remaining items to add individually:', remainingItems);

        formData = {
          items: [
            {
              id: parseInt(bestMatch.combinedProdId, 10),
              quantity: 1,
            },
            ...remainingItems,
          ],
        };
      }
    } else {
      console.log('No matching combined product found, using individual items');
      // No match case - keep using all individual items (already in formData)
    }

    // Ensure free gifts from the Free Gift block are added (do this AFTER combo matching)
    if (Array.isArray(this.freeGiftIds) && this.freeGiftIds.length > 0) {
      const existingIds = new Set(formData.items.map((item) => item.id.toString()));
      this.freeGiftIds.forEach((giftId) => {
        if (giftId && !existingIds.has(giftId.toString())) {
          formData.items.push({
            id: parseInt(giftId, 10),
            quantity: 1,
          });
        }
      });
    }

    console.log('Final form data to be submitted:', formData);

    // Note: Cart threshold reward free products are managed separately in handleFreeProduct()

    // Submit the cart
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then(async (data) => {
        await this.handleFreeProduct();
        window.location.href = window.Shopify.routes.root + 'cart';
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  // add global free product
  async handleFreeProduct() {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      const configEl = document.getElementById('cart-reward-config');
      if (!configEl) return false;
      // Only run when the cart threshold offer exists (eligibility gate)
      var offerEl = document.getElementById('cart-threshold-offer');
      if (!offerEl) return false;

      const { threshold, reward_type, free_variant_id } = JSON.parse(
        configEl.textContent || '{}'
      );
      if (reward_type !== 'free_product' || !free_variant_id) return false;

      const cartTotal = cart.total_price;
      const reached = cartTotal >= threshold;
      const freeInCart = cart.items.some((i) => i.id === free_variant_id);

      // Never auto-add; only auto-remove below threshold
      if (reached && !freeInCart) {
        return false;
      }

      if (!reached && freeInCart) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: free_variant_id, quantity: 0 }),
        });
        return true;
      }

      return false;
    } catch (err) {
      console.error('[CartReward] Error:', err);
      return false;
    }
  }

  // Helper method to determine if an ID is an add-on product and not a main variant
  isAddonProduct(id) {
    // Find the element with this ID
    const element = document.querySelector(`[data-add-on-id="${id}"]`);
    if (!element) return false;

    // Check if it's inside an add-on wrapper
    return (
      element.closest('configurator-add-ons') !== null ||
      element.closest('.add-on-wrapper-flex') !== null
    );
  }
}

customElements.define('configurator-product-form', ConfiguratorProductForm);
